import { getStateAbbreviation } from '../constants/locations';

/**
 * Same footnote map as AnalysisHeader / FootnotesModal when the selected location matches
 * `data.current` naming (city/county/metro + API footnote_1…5 fields).
 *
 * @param {object | null | undefined} selectedLocation - `{ info, data: { current } }`
 * @returns {Record<string, string> | null} e.g. `{ footnote_1: '...', footnote_2: '...' }` or null
 */
export function getLocationFootnotesMap(selectedLocation) {
  if (!selectedLocation?.data?.current) return null;

  const data = selectedLocation.data.current;
  const selectedName = selectedLocation?.info?.name;
  const selectedType = selectedLocation?.info?.type;

  if (!selectedName || !selectedType) return null;

  let dataLocationName = null;

  if (selectedType === 'city' && data.city && data.state) {
    dataLocationName = `${data.city}, ${data.state}`;
  } else if (selectedType === 'county' && data.county && data.state) {
    dataLocationName = (data.county === 'Carson City' && data.state === 'Nevada')
      ? `${data.county}, ${data.state}`
      : data.state === 'Louisiana'
        ? `${data.county} Parish, ${data.state}`
        : data.state === 'Alaska'
          ? `${data.county} Borough, ${data.state}`
          : `${data.county} County, ${data.state}`;
  } else if (data.name) {
    dataLocationName = data.name;
  }

  if (!dataLocationName || selectedName !== dataLocationName) return null;

  const footnotes = {};
  let footnoteIndex = 1;

  if (data.jurisdictions && data.jurisdictions.trim()) {
    const cityName = data.city || 'This city';
    const stateName = data.state || '';
    const stateAbbr = getStateAbbreviation(stateName);
    const year = data.year || '';
    footnotes[`footnote_${footnoteIndex}`] =
      `${cityName}, ${stateAbbr} includes the following jurisdictions for ${year}:\n\n<i>${data.jurisdictions}</i>`;
    footnoteIndex++;
  }

  if (selectedType === 'metro' && data.original_name && data.original_name.trim()) {
    const metroName = data.name || selectedName;
    const originalMetroName = data.original_name;
    const year = data.year || '';
    footnotes[`footnote_${footnoteIndex}`] =
      `The ${metroName} was originally reported by the FBI as ${originalMetroName} in ${year}.`;
    footnoteIndex++;
  }

  if (data.footnote_1 && data.footnote_1.trim()) {
    footnotes[`footnote_${footnoteIndex}`] = data.footnote_1;
    footnoteIndex++;
  }
  if (data.footnote_2 && data.footnote_2.trim()) {
    footnotes[`footnote_${footnoteIndex}`] = data.footnote_2;
    footnoteIndex++;
  }
  if (data.footnote_3 && data.footnote_3.trim()) {
    footnotes[`footnote_${footnoteIndex}`] = data.footnote_3;
    footnoteIndex++;
  }
  if (data.footnote_4 && data.footnote_4.trim()) {
    footnotes[`footnote_${footnoteIndex}`] = data.footnote_4;
    footnoteIndex++;
  }
  if (data.footnote_5 && data.footnote_5.trim()) {
    footnotes[`footnote_${footnoteIndex}`] = data.footnote_5;
    footnoteIndex++;
  }

  const hasFootnotes = Object.values(footnotes).some((footnote) => footnote && footnote.trim());
  return hasFootnotes ? footnotes : null;
}

/** Sorted 1-based indices for raster/header export (matches AnalysisHeader footnote order). */
export function getSortedFootnoteIndicesFromMap(footnotesMap) {
  if (!footnotesMap || !Object.keys(footnotesMap).length) return [];
  const numbers = [];
  Object.keys(footnotesMap).forEach((key) => {
    const num = parseInt(key.replace('footnote_', ''), 10);
    if (!Number.isNaN(num)) numbers.push(num);
  });
  return numbers.sort((a, b) => a - b);
}
